import { normalizePlaylistFilename, serializePlaylist, type PlaylistDocument, type SerializeOptions } from "@protv-converter/core";

export function downloadTextFile(content: string, filename: string): void {
  const objectUrl = URL.createObjectURL(new Blob([content], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = normalizePlaylistFilename(filename);
  link.click();
  URL.revokeObjectURL(objectUrl);
}

async function fetchThumbnail(videoId: string): Promise<Uint8Array | null> {
  try {
    const response = await fetch(`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`);
    if (!response.ok) return null;
    return new Uint8Array(await response.arrayBuffer());
  } catch {
    return null;
  }
}

export async function downloadThumbnailBundle(
  playlistDocument: PlaylistDocument,
  filename: string,
  options: SerializeOptions,
  onProgress: (completed: number, total: number) => void,
): Promise<{ downloaded: number; failed: number }> {
  const candidates = playlistDocument.entries.filter((entry) => entry.youtubeVideoId);
  const files: Record<string, Uint8Array> = {};
  const successfulIds = new Set<string>();
  let completed = 0;

  for (let start = 0; start < candidates.length; start += 6) {
    const batch = candidates.slice(start, start + 6);
    const results = await Promise.all(
      batch.map(async (entry) => ({ entry, bytes: await fetchThumbnail(entry.youtubeVideoId ?? "") })),
    );

    for (const { entry, bytes } of results) {
      completed += 1;
      if (bytes && entry.youtubeVideoId) {
        successfulIds.add(entry.youtubeVideoId);
        files[`Assets/ProTV/PlaylistImages/${entry.youtubeVideoId}.jpg`] = bytes;
      }
      onProgress(completed, candidates.length);
    }
  }

  const bundleDocument: PlaylistDocument = {
    ...playlistDocument,
    entries: playlistDocument.entries.map((entry) =>
      entry.youtubeVideoId && successfulIds.has(entry.youtubeVideoId)
        ? { ...entry, imagePath: `Assets/ProTV/PlaylistImages/${entry.youtubeVideoId}.jpg` }
        : entry,
    ),
  };
  const playlistFilename = normalizePlaylistFilename(filename);
  files[playlistFilename] = new TextEncoder().encode(serializePlaylist(bundleDocument, options));
  files["README.txt"] = new TextEncoder().encode(
    "Extract this archive into the root of your Unity project. Then assign or import the included .playlist file in the ProTV Playlist inspector. The Assets/ProTV/PlaylistImages folder contains the downloaded YouTube thumbnails.\n",
  );

  const { zipSync } = await import("fflate");
  const zipBytes = zipSync(files, { level: 6 });
  const objectUrl = URL.createObjectURL(new Blob([zipBytes], { type: "application/zip" }));
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = playlistFilename.replace(/\.playlist$/i, "-with-thumbnails.zip");
  link.click();
  URL.revokeObjectURL(objectUrl);

  return { downloaded: successfulIds.size, failed: candidates.length - successfulIds.size };
}
